import { Fragment, useState, useRef, useCallback, useEffect } from "react";
import { ToolContainer, FileDropZone, downloadBlob, shareText } from "@/components/ToolLayout";
import { useShareGate } from "@/hooks/useShareGate";
import { Button } from "@/components/ui/button";
import { CameraNotice } from "@/components/CameraNotice";
import { Camera, Upload, CheckCircle2, Copy, Download, Share2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { isStandaloneApp } from "@/lib/mediaPermissions";
import { MediaPermissionAlert } from "@/components/MediaPermissionAlert";
import { BrowserMultiFormatReader, BarcodeFormat } from "@zxing/library";
import { classifyScan, contactToVcf } from "@/lib/qrHelpers";
import { DoorCheck } from "./DoorCheck";
import { looksLikeImage, notAnImageMessage, probeImage, unreadableImageMessage } from "../image/imageCheck";
import { NoticeBox } from "@/components/NoticeBox";

const formatName = (f: BarcodeFormat | undefined) =>
  f === undefined ? "" : String(BarcodeFormat[f] ?? "").replace(/_/g, "-").replace("QR-CODE", "QR code");

const TOOL_INFO_CONTENT = {
  what: `Scans a QR code or barcode using your device's camera or an uploaded image, and decodes the data it contains. If the result is a link, email, or phone number, it's shown as something you can tap or click directly — not just text to read.`,
  how: `Grant camera access and point it at a code, or upload an image containing one. The decoded content appears clearly marked as detected, with options to copy it, download it as a text file, share it, or open it directly if it's a link. Wi-Fi and contact-card codes are read into their fields, links show a warning before you open them, and a contact card can be saved as a .vcf file.`,
  tip: `If scanning from an uploaded photo, make sure the code is reasonably sharp and well-lit — a blurry or heavily angled photo of a code is much harder to decode reliably.`,
};

// Decoding runs through a real, pure-JavaScript decoder (ZXing) rather
// than the browser's own native BarcodeDetector API -- a real,
// confirmed, well-documented problem with that native API: its mere
// presence (`"BarcodeDetector" in window` returning true) does NOT
// mean it actually works. On Chrome specifically, BarcodeDetector's
// real detection logic depends on a native, OS-level shape-detection
// library that has historically only genuinely shipped on Android and
// macOS -- on Windows desktop Chrome, the object can exist while
// detect() reliably returns zero results or throws, exactly matching
// the reported "barcode detection is not working" despite the browser
// technically supporting the API. Verified this directly against
// Chrome's own bug tracker and multiple independent reports before
// switching approaches, rather than assuming. ZXing runs the same
// decoding logic in JavaScript itself, so it works identically and
// reliably across every browser and OS -- no native dependency to be
// silently missing on a given platform.
function createReader() {
  return new BrowserMultiFormatReader();
}

// A decoded QR/barcode value is only worth rendering as a clickable
// link when it's a real, openable URI -- http(s) for the common case
// (most QR codes people scan are links), plus mailto/tel/sms since
// those are genuinely actionable too (a browser will hand them to the
// right app). Anything else (plain text, a product code, a WiFi
// config string) stays as plain text -- turning arbitrary decoded
// text into a clickable href would be misleading, not helpful.
const CLICKABLE_PROTOCOLS = new Set(["http:", "https:", "mailto:", "tel:", "sms:"]);
function getClickableHref(text: string): string | null {
  try {
    const url = new URL(text);
    return CLICKABLE_PROTOCOLS.has(url.protocol) ? text : null;
  } catch {
    return null;
  }
}

export default function QrScanner() {
  const { gateShare } = useShareGate();
  const [result, setResult] = useState("");
  const [format, setFormat] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [fileError, setFileError] = useState("");
  const [scanning, setScanning] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [highlightUpload, setHighlightUpload] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const uploadSectionRef = useRef<HTMLDivElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  const readerRef = useRef<BrowserMultiFormatReader | null>(null);
  const pendingStreamRef = useRef<MediaStream | null>(null);

  // Most-reported complaint this fixes: after a successful scan, the
  // result sat below the fold (especially right after an upload, where
  // the dropzone and any camera preview are still on screen above it),
  // so people assumed the scan silently failed. Scrolling it into view
  // the moment it appears, on both the camera and upload paths, means
  // there's no "did this work?" gap to begin with.
  useEffect(() => {
    if (result) resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [result]);

  const pointToUpload = () => {
    setHighlightUpload(true);
    uploadSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => setHighlightUpload(false), 2500);
  };

  const startScan = async () => {
    setCameraError(null);
    setFileError("");
    setResult("");
    setFormat("");
    setNotFound(false);
    try {
      // facingMode: "environment" (rear camera) can fail with a
      // constraint error on devices that only have a front-facing camera
      // (most laptops) -- that's a genuinely different problem than
      // permission being denied, and was previously misreported as
      // "access denied" regardless of the real cause. Falling back to
      // whatever camera is available instead of failing outright.
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      } catch (constraintErr) {
        if (constraintErr instanceof DOMException && constraintErr.name === "OverconstrainedError") {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
        } else {
          throw constraintErr;
        }
      }
      // The <video> element only exists in the DOM once scanning is
      // true -- at this point it hasn't rendered yet, so videoRef.current
      // would still be null here. Attaching the stream in the effect
      // below (which runs after React has actually mounted the
      // element) is what makes this reliable.
      pendingStreamRef.current = stream;
      setScanning(true);
    } catch (err) {
      if (err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "PermissionDeniedError")) {
        setCameraError(
          isStandaloneApp()
            ? "Camera access was denied for this app. Open freedailypro.com in your regular browser instead and " +
              "try again there, or check your phone's own Settings -> Apps -> FreeDailyPro -> Permissions -> " +
              "Camera — or upload a photo of the code instead."
            : "Camera access was denied. Browsers only show the \"allow camera?\" prompt once — click the lock/info " +
              "icon in your address bar, set Camera to Allow, then reload — or upload a photo of the code instead."
        );
      } else if (err instanceof DOMException && err.name === "NotFoundError") {
        setCameraError("No camera was found on this device. Upload a photo of the code instead.");
      } else if (err instanceof DOMException && err.name === "NotReadableError") {
        // A very common real-world case: permission genuinely was
        // granted, but another app or browser tab already has the
        // camera locked -- this is NOT a permission problem, and was
        // previously indistinguishable from one in the error message,
        // which is exactly what "denied, even though I said yes" looks
        // like from the outside.
        setCameraError("Your camera seems to be in use by another app or browser tab right now. Close whatever else might be using it and try again, or upload a photo of the code instead.");
      } else {
        setCameraError("Couldn't start the camera. Upload a photo of the code instead.");
      }
      pointToUpload();
    }
  };

  // Attaches the pending stream once the <video> element actually
  // exists -- runs right after scanning flips to true and React has
  // mounted the element, which is the earliest point videoRef.current
  // is guaranteed non-null. Only then does continuous decoding
  // actually start, since it needs the real video element wired up
  // first.
  useEffect(() => {
    if (!scanning || !pendingStreamRef.current || !videoRef.current) return;
    const video = videoRef.current;
    const stream = pendingStreamRef.current;
    pendingStreamRef.current = null;
    const reader = createReader();
    readerRef.current = reader;
    reader.decodeFromStream(stream, video, (res, err) => {
      if (res) {
        setResult(res.getText());
        setFormat(formatName(res.getBarcodeFormat()));
        setNotFound(false);
        toast.success("Code detected!");
        stopScan();
      }
      // A per-frame "not found" result (err set, res undefined) is
      // completely normal while scanning -- not a real error, just
      // means this particular frame didn't contain a decodable code
      // yet. Only a successful result should ever stop scanning.
    }).catch((err) => {
      console.error("Video/decoder setup failed:", err);
      setCameraError("Got camera access, but the preview couldn't start playing. Try again, or upload a photo of the code instead.");
      stream.getTracks().forEach(t => t.stop());
      setScanning(false);
    });
  }, [scanning]);

  const stopScan = () => {
    readerRef.current?.reset();
    readerRef.current = null;
    if (videoRef.current?.srcObject) {
      (videoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
    }
    setScanning(false);
  };

  useEffect(() => () => stopScan(), []);

  // Copy and Share take the scanned result off the page, so they go through the export gate like a download.
  const copyResult = () => gateShare(async () => {
    try {
      await navigator.clipboard.writeText(result);
      toast.success("Copied to clipboard");
      return true;
    } catch {
      toast.error("Couldn't copy — your browser may not support clipboard access here.");
      return false;
    }
  });

  const downloadResult = () => {
    downloadBlob(new Blob([result], { type: "text/plain" }), "scanned-code.txt");
  };

  const saveContact = (c: NonNullable<ReturnType<typeof classifyScan>["contact"]>) => {
    downloadBlob(new Blob([contactToVcf(c)], { type: "text/vcard" }), "contact.vcf");
  };

  const shareResult = () => gateShare(async () => {
    const link = getClickableHref(result);
    try {
      const shared = await shareText(result, { title: "Scanned code", url: link ?? undefined });
      if (shared) return true;
    } catch {
      // Covers both a cancelled share (shareText now lets AbortError
      // propagate rather than swallowing it to false) and a real share
      // failure -- either way, fall through to the clipboard copy below
      // rather than leaving the click looking like it did nothing.
    }
    // No Web Share support (most desktop browsers) -- copying is the
    // honest fallback, same pattern ShareButton uses for sharing a link.
    try {
      await navigator.clipboard.writeText(result);
      toast.success("Copied to clipboard");
      return true;
    } catch {
      toast.error("Couldn't copy — your browser may not support clipboard access here.");
      return false;
    }
  });

  const handleUpload = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    // A new file means the old result no longer applies, whatever happens next.
    setResult("");
    setFormat("");
    setNotFound(false);
    setFileError("");
    if (!looksLikeImage(f)) { setFileError(notAnImageMessage(f.name)); return; }
    if (!(await probeImage(f))) { setFileError(unreadableImageMessage(f.name)); return; }
    const url = URL.createObjectURL(f);
    try {
      const reader = createReader();
      const res = await reader.decodeFromImageUrl(url);
      setResult(res.getText());
      setFormat(formatName(res.getBarcodeFormat()));
      toast.success("Code detected!");
    } catch (err) {
      console.error("Decode from image failed:", err);
      setNotFound(true);
      toast.error("No code found in that image — try a sharper, better-lit photo with the code filling more of the frame.");
    } finally {
      URL.revokeObjectURL(url);
    }
  }, []);

  return (
    <ToolContainer>
      <div className="space-y-4 text-center">
        {scanning ? (
          <div className="space-y-4">
            <video ref={videoRef} autoPlay playsInline muted className="w-full max-h-64 rounded-lg border mx-auto" />
            <Button variant="destructive" onClick={stopScan}>Stop Scanning</Button>
          </div>
        ) : (
          <div className="py-8 border-2 border-dashed rounded-xl space-y-3">
            <Camera className="h-12 w-12 mx-auto text-muted-foreground" />
            <CameraNotice />
            <Button onClick={startScan} className="gap-2"><Camera className="h-4 w-4" /> Start Camera Scanner</Button>
            {cameraError && <div className="px-4 max-w-md mx-auto text-left"><MediaPermissionAlert message={cameraError} /></div>}
          </div>
        )}

        <div ref={uploadSectionRef} className={`text-left rounded-lg transition-all ${highlightUpload ? "ring-2 ring-primary ring-offset-2" : ""}`}>
          <p className="text-sm font-medium mb-2 flex items-center gap-1.5"><Upload className="h-3.5 w-3.5" /> Or upload an image</p>
          <FileDropZone gateAtDownload accept="image/*" onFiles={handleUpload} label="Drop an image with a code to scan" enableCamera={false} />
        </div>

        {fileError && <p role="alert" className="text-xs text-destructive text-left" data-testid="scan-file-error">{fileError}</p>}

        {notFound && !result && (
          <div role="alert" data-testid="scan-not-found" className="p-4 rounded-xl border-2 border-destructive/40 bg-destructive/5 text-left text-sm">
            <p className="font-semibold">No code found</p>
            <p className="text-muted-foreground">Try a sharper, better-lit photo with the code filling more of the frame.</p>
          </div>
        )}

        {result && (() => {
          const link = getClickableHref(result);
          const info = classifyScan(result);
          return (
            <div ref={resultRef} data-testid="scan-result" className="p-5 rounded-xl border-2 border-primary bg-primary/5 text-left space-y-3">
              <p className="text-sm font-semibold flex items-center gap-1.5 text-primary">
                <CheckCircle2 className="h-4 w-4" /> Code detected{format && <span className="font-normal text-muted-foreground" data-testid="scan-format"> · {format}</span>}
              </p>
              <DoorCheck code={result} />
              {info.kind === "wifi" && info.wifi && (
                <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">WiFi network</dt><dd className="font-medium break-all">{info.wifi.ssid}</dd>
                  <dt className="text-muted-foreground">Security</dt><dd>{info.wifi.security === "nopass" ? "Open (no password)" : info.wifi.security}{info.wifi.hidden ? ", hidden network" : ""}</dd>
                  {info.wifi.password && (<><dt className="text-muted-foreground">Password</dt><dd className="font-mono break-all">{info.wifi.password}</dd></>)}
                </dl>
              )}
              {info.kind === "vcard" && info.contact && (
                <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">Name</dt><dd className="font-medium">{info.contact.name}</dd>
                  {info.contact.org && (<><dt className="text-muted-foreground">Company</dt><dd>{info.contact.org}</dd></>)}
                  {info.contact.phones.map((p, i) => (<Fragment key={"p" + i}><dt className="text-muted-foreground">Phone</dt><dd><a className="underline" href={`tel:${p}`}>{p}</a></dd></Fragment>))}
                  {info.contact.emails.map((e, i) => (<Fragment key={"e" + i}><dt className="text-muted-foreground">Email</dt><dd><a className="underline" href={`mailto:${e}`}>{e}</a></dd></Fragment>))}
                  {info.contact.url && (<><dt className="text-muted-foreground">Website</dt><dd className="break-all">{info.contact.url}</dd></>)}
                </dl>
              )}
              {(info.kind === "email" || info.kind === "sms" || info.kind === "phone") && (
                <dl className="text-sm grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <dt className="text-muted-foreground">{info.kind === "email" ? "Email to" : info.kind === "sms" ? "Text to" : "Phone"}</dt><dd className="font-medium">{info.to}</dd>
                  {info.subject && (<><dt className="text-muted-foreground">Subject</dt><dd>{info.subject}</dd></>)}
                  {info.body && (<><dt className="text-muted-foreground">Message</dt><dd className="break-words">{info.body}</dd></>)}
                </dl>
              )}
              {info.kind === "url" && info.url && (
                <p className="text-sm">Opens <strong className="break-all">{info.url.hostname}</strong></p>
              )}
              {info.warning && <NoticeBox className="text-sm" role="alert" testId="scan-warning">{info.warning}</NoticeBox>}

              {link ? (
                <a
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-base font-medium break-all text-primary underline underline-offset-2 hover:opacity-80 inline-flex items-start gap-1.5"
                >
                  {result}
                  <ExternalLink className="h-3.5 w-3.5 mt-1 shrink-0" />
                </a>
              ) : info.kind === "text" ? (
                <p className="text-base font-medium font-mono break-all">{result}</p>
              ) : (
                <details className="text-xs text-muted-foreground"><summary className="cursor-pointer">Raw text</summary><pre className="whitespace-pre-wrap break-all font-mono mt-1">{result}</pre></details>
              )}
              <div className="flex flex-wrap gap-2 pt-1">
                {info.kind === "vcard" && info.contact && (
                  <Button size="sm" className="gap-1.5" onClick={() => saveContact(info.contact!)}>
                    <Download className="h-3.5 w-3.5" /> Save contact (.vcf)
                  </Button>
                )}
                <Button size="sm" variant="outline" className="gap-1.5" onClick={copyResult}>
                  <Copy className="h-3.5 w-3.5" /> Copy
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={downloadResult}>
                  <Download className="h-3.5 w-3.5" /> Download .txt
                </Button>
                <Button size="sm" variant="outline" className="gap-1.5" onClick={shareResult}>
                  <Share2 className="h-3.5 w-3.5" /> Share
                </Button>
              </div>
            </div>
          );
        })()}
      </div>
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
